/**
* Nom : companyEdit.js
* Description : Ce fichier contient la définition de la page companyEdit 
*               Cette page fourni un formulaire pour mettre à jour les informations d'une société
* Date de création : 7 juillet 2025
*/

/* CSS et assets */
// None

/* Bibliothèques et composants */
import { CompanyBar } from 'package_ui_ihm/dist'
import { TabC } from 'package_ui_ihm/dist'
import { Container } from 'package_ui_ihm/dist'
import { BackPage } from 'package_ui_ihm/dist'
import { InputForm } from 'package_ui_ihm/dist'

/* Hook */
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useBlocker } from 'react-router-dom'
import { useAuth } from 'package_ui_ihm/dist'
import { useApi } from 'package_ui_ihm/dist'
import { useNavigate } from 'react-router-dom'
import { useScreenWidth } from 'package_ui_ihm/dist'


/**
* CompanyEdit : affiche une page avec un formulaire pour mettre à jour les informations d'une société
*               un lien permet de revenir sur la page qui liste toutes les sociétés 
*
* Type : page 
*
* Parametres : 
*  None
*/
function CompanyEdit() {

    //Define useParam
    const { id } = useParams()


    // Define useState hooks
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [code, setCode] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [oldName, setOldName] = useState('');
    const [oldAddress, setOldAddress] = useState('');
    const [oldCode, setOldCode] = useState('');
    const [oldCity, setOldCity] = useState('');
    const [oldCountry, setOldCountry] = useState('');
    const [oldCompany, setOldCompany] = useState(null)
    const [statusOldCompany, setStatusOldCompany] = useState(null)
    const [statusNewCompany, setStatusNewCompany] = useState(null)
    const [nameError, setNameError] = useState(false);
    const [addressError, setAddressError] = useState(false);
    const [codeError, setCodeError] = useState(false);
    const [cityError, setCityError] = useState(false);
    const [countryError, setCountryError] = useState(false);
    const [isDataLoading, setDataLoading] = useState(false)
    const [skipPrompt, setSkipPrompt] = useState(false);


    // Define useContext hooks
    const { authStatus, authToken } = useAuth();
    const { path } = useApi();

    const navigate = useNavigate();


    // Define useBlocker hooks
    const shouldBlock = 
        ((name !== oldName) || 
        (address !== oldAddress) || 
        (code !== oldCode) ||
        (city !== oldCity) ||
        (country !== oldCountry)) && 
        (skipPrompt === false);
    const blocker = useBlocker(shouldBlock);


    // Define useScreenWidth hooks
    const screenWidth = useScreenWidth();


    // Define functions
    async function sendData(name, address, code, city, country) {
        // Token
        const token = "Bearer " + authToken
        // Création du Form Data
        let form_data = new FormData();
        form_data.append("name", name);
        form_data.append("address", address);
        form_data.append("code", code);
        form_data.append("city", city);
        form_data.append("country", country);
        // Update company
        const response = await fetch(path + "/api/au-cony/" + id + "/", 
            {
                method: 'PATCH',
                credentials: 'include',
                mode: 'cors',
                headers: {
                    accept: 'application/json',
                    //'Content-Type': 'multipart/form-data',  cette ligne ne doit pas être utilisée avec FormData sinon erreur
                    Authorization: token,
                },
                body: form_data
            }
        )
        setStatusNewCompany(response["status"])
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        setSkipPrompt(true);
        if(name === null || name === ''){
            setNameError(true)
        }
        else
        {   
            setNameError(false)
        }
        if(address === null || address === ''){
            setAddressError(true)
        }
        else
        {   
            setAddressError(false)
        }
        if(code === null || code === ''){
            setCodeError(true)
        }
        else
        {   
            setCodeError(false)
        }
        if(city === null || city === ''){
            setCityError(true)
        }
        else
        {   
            setCityError(false)
        }
        if(country === null || country === ''){
            setCountryError(true)
        }
        else
        {   
            setCountryError(false)
        }
        if(nameError === false && addressError === false && codeError === false && cityError === false && countryError === false){
            sendData(name, address, code, city, country)
        }
    };

    async function retrieveData() {
        // Token
        const token = "Bearer " + authToken
        // Retrieve company details
        const response = await fetch(path + "/api/au-cony/" + id + "/", 
            {
                method: 'GET',
                credentials: 'include',
                mode: 'cors',
                headers: {
                    accept: 'application/json',
                    Authorization: token,
                },
            }
        );
        const data = await response.json();
        setStatusOldCompany(response["status"])
        setOldCompany(data)
        setName(data["name"])
        setAddress(data["address"])
        setCode(data["code"])
        setCity(data["city"])
        setCountry(data["country"])
        setOldName(data["name"])
        setOldAddress(data["address"])
        setOldCode(data["code"])
        setOldCity(data["city"])
        setOldCountry(data["country"])
        // Dataloading
        setDataLoading(false)
    }


    // Define useEffect hooks
    useEffect(() => {
        if(authStatus === 200)
        {
            setDataLoading(true)
            retrieveData()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if(statusNewCompany === 200) {
            navigate('/companies/')
        } else {
            setSkipPrompt(false);
            const target_path = '/companies/' + id + "/edit"
            navigate(target_path)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusNewCompany, navigate]);

    useEffect(() => {
        // Bloquer la fermeture d'onglet/rafraîchissement
        const handleBeforeUnload = (e) => {
            if (shouldBlock === true) {
                // Standard pour afficher une confirmation (les navigateurs modernes gèrent le message)
                e.preventDefault();
                e.returnValue = "";
                // Certains navigateurs nécessitent cette ligne pour afficher la confirmation
                return (e.returnValue = "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées.");
            }
        }; 

        window.addEventListener("beforeunload", handleBeforeUnload);

        // Nettoyer l'écouteur quand le composant est démonté
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldBlock]);

    useEffect(() => {
        if (blocker.state === "blocked") {
            const confirmed = window.confirm(
                "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées."
            );

            if (confirmed) {
                blocker.proceed();
            } else {
                blocker.reset();
            }
        }
    }, [blocker]);


    // Render component
    return (
        <div>
            <CompanyBar name_app="BOXIBRAIN"/>
            <TabC tabNumber={5}/>
            <div className={(screenWidth > 650) ? 'App' : 'App-ss'}>
                <Container>
                    {isDataLoading ? (
                        <p>Chargement des données</p>
                    ) : (
                        oldCompany ? (
                            <div>
                                <h1>Modifier la société {oldCompany["name"]}</h1>
                                {/*Lien de retour*/}
                                <div className='flex-row justify-start margin-left margin-bottom margin-top'>
                                    <div className='link-border'>
                                        <BackPage path={'/companies/'}>Retour à la liste des sociétés</BackPage>
                                    </div>
                                </div>
                                <div>
                                    {(statusNewCompany !== null && statusNewCompany !== 200 && nameError === false && addressError === false && codeError === false && cityError === false && countryError === false) ? (
                                        <p className='form-error'>La société existe déjà !</p>
                                    ) : (
                                        <p/>
                                    )}
                                </div>
                                <form onSubmit={handleSubmit} className='flex-column'>
                                    <div className='flex-column form-center'>
                                        <InputForm error={nameError} type="text" label="Nom" value={name} setValue={setName} disabled={false}/>
                                                                    <InputForm error={addressError} type="text" label="Numéro et nom de rue" value={address} setValue={setAddress} disabled={false}/>
                                                                    <InputForm error={codeError} type="text" label="Code postal" value={code} setValue={setCode} disabled={false}/>
                                                                    <InputForm error={cityError} type="text" label="Ville" value={city} setValue={setCity} disabled={false}/>
                                                                    <InputForm error={countryError} type="text" label="Pays" value={country} setValue={setCountry} disabled={false}/>
                                    </div>
                                    <div className='flex-row justify-end'>
                                        <button type="submit" className='button'>Valider</button>
                                    </div>
                                </form> 
                            </div>
                        ) : (
                            <p>Société non disponible {statusOldCompany} !</p>
                        )
                    )}
                </Container>
            </div>
        </div>
    );
}

export default CompanyEdit;
